import React, { useEffect, useState } from "react";
import { Card, Tabs, Typography, Spin, Statistic, Row, Col } from "antd";
import {
  UserOutlined,
  HistoryOutlined,
  DollarOutlined,
  DashboardOutlined,
  TeamOutlined,
  CrownOutlined,
  KeyOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";
import { saasControlService, DashboardStats } from "~/services/saas-control";
import { UsersTab } from "./users-tab";
import { SessionsTab } from "./sessions-tab";
import { CustomersTab } from "./customers-tab";
import { PaymentsTab } from "./payments-tab";
import { InviteCodesTab } from "./invite-codes-tab";
import { TenantsTab } from "./tenants-tab";

const { Title } = Typography;

export const SaasControl: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoadingStats(true);
      const data = await saasControlService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error("Failed to load dashboard stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Only show if current user is SUPER_ADMIN
  if (currentUser?.role !== Role.SUPER_ADMIN) {
    return (
      <Card>
        <div className="text-center py-8">
          <UserOutlined className="text-4xl text-gray-400 mb-4" />
          <Title level={4} className="text-gray-500">
            You don't have permission to access this page
          </Title>
        </div>
      </Card>
    );
  }

  const tabItems = [
    {
      key: "dashboard",
      label: (
        <span>
          <DashboardOutlined />
          Dashboard
        </span>
      ),
      children: (
        <div className="p-4">
          {loadingStats ? (
            <div className="flex justify-center py-8">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <Title level={5} className="mb-4">
                Internal Team
              </Title>
              <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Team Members"
                      value={stats?.internalUsersCount || 0}
                      prefix={<TeamOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Active Sessions"
                      value={stats?.activeSessionsCount || 0}
                      prefix={<HistoryOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
              </Row>

              <Title level={5} className="mb-4">
                Tenants
              </Title>
              <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Total Tenants"
                      value={stats?.tenantsCount || 0}
                      prefix={<ShopOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Paying Tenants"
                      value={stats?.payingTenantsCount || 0}
                      prefix={<CrownOutlined />}
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Total Payments"
                      value={stats?.totalPaymentsAmount || 0}
                      prefix={<DollarOutlined />}
                      precision={2}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Card>
                </Col>
              </Row>

              <Title level={5} className="mb-4">
                SaaS Customers
              </Title>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Total Customers"
                      value={stats?.saasCustomersCount || 0}
                      prefix={<UserOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Paying Customers"
                      value={stats?.payingCustomersCount || 0}
                      prefix={<CrownOutlined />}
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>
      ),
    },
    {
      key: "team",
      label: (
        <span>
          <TeamOutlined />
          Team
        </span>
      ),
      children: <UsersTab />,
    },
    {
      key: "sessions",
      label: (
        <span>
          <HistoryOutlined />
          Sessions
        </span>
      ),
      children: <SessionsTab />,
    },
    {
      key: "tenants",
      label: (
        <span>
          <ShopOutlined />
          Tenants
        </span>
      ),
      children: <TenantsTab />,
    },
    {
      key: "customers",
      label: (
        <span>
          <CrownOutlined />
          Customers
        </span>
      ),
      children: <CustomersTab />,
    },
    {
      key: "payments",
      label: (
        <span>
          <DollarOutlined />
          Payments
        </span>
      ),
      children: <PaymentsTab />,
    },
    {
      key: "invite-codes",
      label: (
        <span>
          <KeyOutlined />
          Invite Codes
        </span>
      ),
      children: <InviteCodesTab />,
    },
  ];

  return (
    <div className="p-4">
      <Card>
        <div className="mb-4">
          <Title level={3} className="mb-0">
            SaaS Control Panel
          </Title>
          <p className="text-gray-500 mt-1">
            Manage your team, track sessions, and monitor customer subscriptions
          </p>
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size="large"
        />
      </Card>
    </div>
  );
};
